import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { loadAircraftCatalog } from '../data/loadAircraftCatalog'
import { loadZoneGeometry } from '../data/loadZoneGeometry'
import { generateSyntheticInquiries } from '../data/syntheticInquirySource'
import { useEventPlannerStore } from '../store/eventPlannerStore'
import { runEngineSelfChecks } from '../engine/selfCheck'
import { runTaxiSelfChecks } from '../data/taxiSelfCheck'
import { runFuelSelfChecks } from '../motion/fuelSelfCheck'
import { runMotionSelfChecks } from '../motion/motionSelfCheck'
import { runFieldGraphSelfChecks } from '../data/fieldGraphSelfCheck'

// The synthetic fleet comes from the active scenario's options, held (and
// persisted) in the planner store so the operator can edit count/mix/curves/seed
// from the Scenario panel. With time-aware spot reuse, each fixed slot is reused
// across the event as aircraft depart, so total placed far exceeds concurrent
// capacity — "Place all" demonstrates the reuse win (placed ≫ peak concurrent).

/**
 * Load the aircraft catalog + KAGS zone geometry, generate the synthetic
 * inquiry set, and seed the planner store's runtime data. Persisted placement
 * progress (localStorage) survives — only runtime data is (re)seeded.
 */
export function useEventBootstrap(
  locationId: string | undefined,
  mapCenter: { lat: number; lng: number } | undefined
) {
  const catalogQ = useQuery({
    queryKey: ['masters-planner', 'catalog'],
    queryFn: loadAircraftCatalog,
    staleTime: Infinity,
  })

  const zonesQ = useQuery({
    queryKey: ['masters-planner', 'zones', locationId, mapCenter?.lat, mapCenter?.lng],
    queryFn: () => loadZoneGeometry(locationId!, mapCenter!),
    enabled: !!locationId && !!mapCenter,
    staleTime: Infinity,
  })

  const hydrateRuntime = useEventPlannerStore((s) => s.hydrateRuntime)
  const ready = useEventPlannerStore((s) => s.ready)

  useEffect(() => {
    if (!catalogQ.data || !zonesQ.data) return
    // Read options imperatively so editing the scenario doesn't re-trigger this
    // bootstrap effect — applying a scenario goes through `regenerateFleet()`.
    const options = useEventPlannerStore.getState().scenarioOptions
    const inquiries = generateSyntheticInquiries(catalogQ.data, options)
    hydrateRuntime(inquiries, zonesQ.data, catalogQ.data)
  }, [catalogQ.data, zonesQ.data, hydrateRuntime])

  // Dev-only: run the pure-engine self-checks once (no test runner in this project).
  useEffect(() => {
    if (!import.meta.env.DEV) return
    const results = [
      ...runEngineSelfChecks(),
      ...runTaxiSelfChecks(),
      ...runFuelSelfChecks(),
      ...runMotionSelfChecks(),
      ...runFieldGraphSelfChecks(),
    ]
    const failed = results.filter((r) => !r.pass)
    if (failed.length) {
      console.error('[event-planner] self-checks FAILED', failed, results)
    } else {
      console.info('[event-planner] self-checks passed', results.length)
    }
  }, [])

  return {
    loading: catalogQ.isLoading || zonesQ.isLoading,
    ready,
    error: catalogQ.error ?? zonesQ.error ?? null,
  }
}
